import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import Header from '../Header';
import SEO from '../SEO';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  );
}

function StatBox({ amount, label, source }: { amount: string; label: string; source: string }) {
  return (
    <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 2, textAlign: 'center' }}>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.2rem', color: '#ff6b6b', lineHeight: 1, mb: 0.5 }}>{amount}</Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'rgba(148,153,176,0.6)' }}>{source}</Typography>
    </Box>
  );
}

export default function BlogCostPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="The Real Cost of One Bad Hire at a Daycare" description="A single abuse incident at a daycare facility can cost $500K–$2M+ in legal fees, settlements, and lost enrollment. Here's what the data actually shows." path="/blog/cost-of-one-bad-hire" type="article" />
      <Header onBookDemo={() => navigate('/contact')} />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, px: 3 }}>

        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', gap: 1, mb: 4, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/blog')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', cursor: 'pointer', '&:hover': { color: '#5ce1e6' } }}>Blog</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.4)' }}>›</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>Financial Risk</Typography>
        </Box>

        {/* Header */}
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 1, display: 'inline-block', mb: 2.5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#ff6b6b', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Financial Risk · 7 min read</Typography>
        </Box>
        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 3, lineHeight: 1.1 }}>
          The True Cost of One Bad Hire in Childcare: From Day 1 to Day 365
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 5, lineHeight: 1.65 }}>
          A single abusive employee costs far more than their salary. We break down the real financial spiral — and show you the math on prevention.
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Section title="Day 1: The incident nobody saw coming">
          <Para>In February 2025, a preschool teacher in Centennial, Colorado was charged with 51 counts of child abuse and harassment against 2-year-olds. Fifty-one. The abuse was systemic, repeated, and — by every account — invisible to everyone until it wasn't.</Para>
          <Para>This is the pattern. It's almost never one dramatic incident. It's a slow accumulation of small violations, each one invisible in isolation, that compound over weeks and months before they become a front-page story.</Para>
          <Para>The question isn't whether your facility could face this. The question is whether you'd catch it before count 10 — or count 51.</Para>
        </Section>

        <Section title="The financial spiral: what actually happens">
          <Para>Most daycare operators think about abuse in terms of criminal charges. That's the smallest part of the financial impact. Here's the full cascade:</Para>
          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              { phase: 'Week 1–2', cost: 'Enrollment collapse', detail: 'Parents pull children the moment a story breaks. You can lose 30–50% of enrollment before a single charge is filed.' },
              { phase: 'Month 1', cost: 'Civil lawsuit filed', detail: 'The family retains an attorney. The suit typically names you personally, not just the LLC. Your personal assets are now in play.' },
              { phase: 'Month 1–3', cost: 'Criminal defense (yours)', detail: 'If you\'re accused of negligent supervision, you need a criminal defense attorney. That\'s $300–500/hr, often requiring a $50,000+ retainer.' },
              { phase: 'Month 3–6', cost: 'State investigation', detail: 'Your license is under review. Inspectors visit unannounced. You\'re paying to operate under scrutiny with fewer children enrolled.' },
              { phase: 'Month 6–18', cost: 'Discovery and trial prep', detail: 'Your attorney bills hours reviewing footage, interviewing witnesses, preparing depositions. Every hour is $400+.' },
              { phase: 'Settlement or verdict', cost: '$10,000 to $16,000,000', detail: 'Physical abuse settlements range from $50K for minor injuries to $16M for the worst outcomes. Most independent operators aren\'t insured for this range.' },
            ].map(({ phase, cost, detail }) => (
              <Box key={phase} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#ff6b6b', fontWeight: 600 }}>{phase}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#f0f0f8', fontWeight: 700 }}>{cost}</Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>
        </Section>

        <Section title="The numbers from real cases">
          <Para>These aren't hypotheticals. These are settled cases from 2021–2025:</Para>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <StatBox amount="$16M" label="SC infant death settlement (2024)" source="Benton Law Firm" />
            <StatBox amount="$25M" label="Memphis lawsuit for permanent injuries (filed 2025)" source="Local news reports" />
            <StatBox amount="$475K" label="NYC brain damage from choking (2021)" source="Bob Katz Law" />
            <StatBox amount="$262K" label="Average physical abuse settlement, NC (2024)" source="Benton Law Firm" />
          </Stack>
          <Para>The average independent daycare generates $300,000–$500,000 in annual revenue. A $262,000 settlement is roughly a year of profit. A $16M judgment is liquidation.</Para>
        </Section>

        <Section title="The hidden costs nobody counts">
          <Para>Beyond the lawsuit, there's a second wave of financial damage that persists for years:</Para>
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {['Insurance cancellation or 300%+ premium increases', 'Google and Yelp reviews that permanently depress enrollment', 'Regulatory fines from licensing authorities', 'Loss of state subsidy contracts (if applicable)', 'Staff turnover from demoralization and uncertainty', 'Rebranding and PR costs if you survive at all'].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#ff6b6b', mt: 0.7, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
              </Box>
            ))}
          </Stack>
        </Section>

        <Section title="The prevention math">
          <Para>SMCLA's AI monitoring system runs continuously — watching behavioral patterns, flagging anomalies, alerting you before a pattern becomes a lawsuit.</Para>
          <Para>Let's do the math. If the average physical abuse settlement is $262,000, and SMCLA prevents one such incident over its lifetime, the ROI is clear. Even at the low end — a $50,000 settlement — prevention wins by a wide margin.</Para>
          <Box sx={{ p: 3, bgcolor: 'rgba(92,225,230,0.04)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2, mb: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#5ce1e6', mb: 1 }}>
              "SMCLA costs less per month than one hour of your defense attorney's time."
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>
              It's the cheapest insurance policy you'll ever buy — and the only one that prevents the incident, not just pays after it.
            </Typography>
          </Box>
        </Section>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Box sx={{ p: 4, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1 }}>
            Calculate your risk exposure
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3, lineHeight: 1.65 }}>
            Book a 30-minute walkthrough and see how SMCLA would work in your specific facility — number of rooms, staff count, and locations.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#4ecfd4' } }}>
            Book a Demo →
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
